import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../supabaseClient";
import "./PaymentProviderManager.css";

const APPROVAL_OPTIONS = ["draft", "pending", "approved", "restricted"];
const ENVIRONMENT_OPTIONS = ["sandbox", "live"];
const ACCESS_OPTIONS = ["disabled", "admin_only", "public"];

const labelize = (value) => String(value || "").replaceAll("_", " ");

export default function PaymentProviderManager() {
  const [providers, setProviders] = useState([]);
  const [selectedKey, setSelectedKey] = useState("paypal");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const [draft, setDraft] = useState(null);
  const [connectionTest, setConnectionTest] = useState(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [globalAccessMode, setGlobalAccessMode] = useState("admin_only");
  const [globalAccessDraft, setGlobalAccessDraft] = useState("admin_only");

  const load = useCallback(async () => {
    setLoading(true);
    setNotice(null);
    const { data, error } = await supabase.rpc("admin_get_payment_providers");
    if (error) {
      setNotice({ type: "error", text: error.message || "Unable to load payment providers." });
      setLoading(false);
      return;
    }
    const rows = Array.isArray(data) ? data : [];
    setProviders(rows);
    const returnedGlobalMode = rows[0]?.global_checkout_access_mode || "admin_only";
    setGlobalAccessMode(returnedGlobalMode);
    setGlobalAccessDraft(returnedGlobalMode);
    if (!rows.some((row) => row.provider_key === selectedKey) && rows[0]?.provider_key) {
      setSelectedKey(rows[0].provider_key);
    }
    setLoading(false);
  }, [selectedKey]);

  useEffect(() => { load(); }, [load]);

  const selected = useMemo(
    () => providers.find((row) => row.provider_key === selectedKey) || null,
    [providers, selectedKey]
  );

  useEffect(() => {
    if (!selected) return;
    setDraft({
      environment: selected.environment || "sandbox",
      approval_status: selected.approval_status || "draft",
      access_mode: selected.access_mode || (selected.marketplace_enabled ? "public" : "disabled"),
      notes: selected.notes || "",
    });
  }, [selected]);

  const totals = useMemo(() => ({
    providers: providers.length,
    publicProviders: providers.filter((p) => p.access_mode === "public").length,
    adminOnlyProviders: providers.filter((p) => p.access_mode === "admin_only").length,
    connected: providers.reduce((sum, p) => sum + Number(p.connected_accounts || 0), 0),
    ready: providers.reduce((sum, p) => sum + Number(p.payment_ready_accounts || 0), 0),
  }), [providers]);

  const testConnection = async () => {
    if (!selected || selected.provider_key !== "paypal") return;
    setTestingConnection(true);
    setConnectionTest(null);
    try {
      const { data, error } = await supabase.functions.invoke("paypal-marketplace", {
        body: { action: "oauth-check" },
      });
      if (error) throw error;
      if (!data?.success || data?.authenticated !== true) {
        throw new Error(data?.error || "PayPal sandbox authentication failed.");
      }
      setConnectionTest({
        type: "success",
        text: `Connected to PayPal ${labelize(data.environment || "sandbox")}.`,
        expiresInSeconds: data.expiresInSeconds ?? null,
        appId: data.appId || null,
      });
    } catch (error) {
      let message = error?.message || "Unable to test PayPal connection.";
      const response = error?.context;
      if (response && typeof response.json === "function") {
        try {
          const body = await response.json();
          if (body?.error) message = body.error;
        } catch {
          // Keep the safe client error message.
        }
      }
      setConnectionTest({ type: "error", text: message });
    } finally {
      setTestingConnection(false);
    }
  };

  const saveGlobalAccess = async () => {
    setBusy(true);
    setNotice(null);
    const { error } = await supabase.rpc("admin_set_marketplace_checkout_access", {
      p_access_mode: globalAccessDraft,
    });
    if (error) {
      setNotice({ type: "error", text: error.message || "Unable to update global checkout access." });
    } else {
      setNotice({ type: "success", text: `Marketplace checkout access set to ${labelize(globalAccessDraft)}.` });
      await load();
    }
    setBusy(false);
  };

  const save = async () => {
    if (!selected || !draft) return;
    setBusy(true);
    setNotice(null);
    const { error } = await supabase.rpc("admin_update_payment_provider", {
      p_provider_key: selected.provider_key,
      p_environment: draft.environment,
      p_approval_status: draft.approval_status,
      p_marketplace_enabled: draft.access_mode === "public",
      p_notes: draft.notes.trim() || null,
      p_access_mode: draft.access_mode,
    });
    if (error) {
      setNotice({ type: "error", text: error.message || "Unable to update provider." });
    } else {
      setNotice({ type: "success", text: `${selected.display_name} settings updated.` });
      await load();
    }
    setBusy(false);
  };

  return (
    <div className="payment-provider-admin">
      {notice && <div className={`payment-provider-notice ${notice.type}`}>{notice.text}</div>}

      <section className="payment-provider-kpis">
        <div><span>Providers</span><strong>{totals.providers}</strong><small>Marketplace integrations tracked</small></div>
        <div><span>Public providers</span><strong>{totals.publicProviders}</strong><small>{totals.adminOnlyProviders} admin-only testing</small></div>
        <div><span>Connected sellers</span><strong>{totals.connected}</strong><small>Connected provider accounts</small></div>
        <div><span>Payment ready</span><strong>{totals.ready}</strong><small>Connected + payments enabled</small></div>
      </section>

      <section className="payment-provider-panel payment-provider-global-access">
        <div className="payment-provider-heading">
          <div>
            <h2>Marketplace checkout access</h2>
            <p>Global emergency gate above every payment provider. Provider access can never be broader than this setting.</p>
          </div>
          <span className={`payment-provider-pill ${globalAccessMode === "public" ? "good" : ""}`}>{labelize(globalAccessMode)}</span>
        </div>
        <div className="payment-provider-global-row">
          <label>Global access
            <select value={globalAccessDraft} onChange={(e) => setGlobalAccessDraft(e.target.value)}>
              {ACCESS_OPTIONS.map((v) => <option key={v} value={v}>{labelize(v)}</option>)}
            </select>
          </label>
          <div className="payment-provider-info">
            Hobbli is currently being built on a live site. Keep this at <strong>Admin only</strong> while checkout is under development. A provider must also independently allow the current user.
          </div>
          <button type="button" className="primary" onClick={saveGlobalAccess} disabled={busy || globalAccessDraft === globalAccessMode}>
            Save global access
          </button>
        </div>
      </section>

      <section className="payment-provider-layout">
        <div className="payment-provider-panel">
          <div className="payment-provider-heading">
            <div><h2>Marketplace providers</h2><p>Provider registry for seller-connected marketplace payments.</p></div>
            <button type="button" onClick={load} disabled={loading}>Refresh</button>
          </div>
          <div className="payment-provider-list">
            {providers.map((provider) => (
              <button
                type="button"
                key={provider.provider_key}
                className={selectedKey === provider.provider_key ? "active" : ""}
                onClick={() => setSelectedKey(provider.provider_key)}
              >
                <span><strong>{provider.display_name}</strong><small>{labelize(provider.environment)} · {labelize(provider.approval_status)}</small></span>
                <span className={`payment-provider-pill ${provider.access_mode === "public" ? "good" : ""}`}>
                  {labelize(provider.access_mode || "disabled")}
                </span>
              </button>
            ))}
            {!providers.length && <div className="payment-provider-empty">{loading ? "Loading providers…" : "No providers configured."}</div>}
          </div>
        </div>

        <div className="payment-provider-stack">
          {selected && draft ? (
            <>
              <div className="payment-provider-panel">
                <div className="payment-provider-heading">
                  <div><h2>{selected.display_name}</h2><p>{selected.description || "Marketplace payment provider."}</p></div>
                  <span className="payment-provider-pill">{selected.provider_key}</span>
                </div>
                <div className="payment-provider-stats">
                  <div><span>Environment</span><strong>{labelize(selected.environment)}</strong></div>
                  <div><span>Approval</span><strong>{labelize(selected.approval_status)}</strong></div>
                  <div><span>Connected</span><strong>{selected.connected_accounts || 0}</strong></div>
                  <div><span>Payment ready</span><strong>{selected.payment_ready_accounts || 0}</strong></div>
                  <div><span>Payout ready</span><strong>{selected.payout_ready_accounts || 0}</strong></div>
                  <div><span>Default accounts</span><strong>{selected.default_accounts || 0}</strong></div>
                </div>
              </div>

              <div className="payment-provider-panel">
                <div className="payment-provider-heading">
                  <div><h3>Provider configuration</h3><p>Non-secret platform controls only. API credentials remain server-side.</p></div>
                </div>
                <div className="payment-provider-form">
                  <label>Environment<select value={draft.environment} onChange={(e) => setDraft({ ...draft, environment: e.target.value })}>{ENVIRONMENT_OPTIONS.map((v) => <option key={v} value={v}>{labelize(v)}</option>)}</select></label>
                  <label>Platform approval<select value={draft.approval_status} onChange={(e) => setDraft({ ...draft, approval_status: e.target.value })}>{APPROVAL_OPTIONS.map((v) => <option key={v} value={v}>{labelize(v)}</option>)}</select></label>
                  <label>Marketplace access<select value={draft.access_mode} onChange={(e) => setDraft({ ...draft, access_mode: e.target.value })}>{ACCESS_OPTIONS.map((v) => <option key={v} value={v}>{labelize(v)}</option>)}</select><small>Disabled blocks everyone. Admin only permits Hobbli administrators. Public permits eligible buyers.</small></label>
                  <label className="wide">Internal notes<textarea value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} placeholder="Approval, rollout, or integration notes" /></label>
                </div>
                <div className="payment-provider-actions"><button type="button" className="primary" onClick={save} disabled={busy}>{busy ? "Saving…" : "Save provider"}</button></div>
              </div>

              <div className="payment-provider-panel">
                <div className="payment-provider-heading"><div><h3>Connection health</h3><p>Aggregated from provider-neutral shop payment accounts.</p></div></div>
                <div className="payment-provider-health">
                  <div><strong>{selected.connected_accounts || 0}</strong><span>connected</span></div>
                  <div><strong>{selected.payment_ready_accounts || 0}</strong><span>payments enabled</span></div>
                  <div><strong>{selected.payout_ready_accounts || 0}</strong><span>payouts enabled</span></div>
                  <div><strong>{selected.disconnected_accounts || 0}</strong><span>disconnected</span></div>
                </div>
                {selected.provider_key === "paypal" && (
                  <>
                    <div className="payment-provider-connection-test">
                      <div>
                        <strong>PayPal API connection</strong>
                        <span>Server-side OAuth health check. Credentials and access tokens are never returned to the browser.</span>
                      </div>
                      <button type="button" className="primary" onClick={testConnection} disabled={testingConnection}>
                        {testingConnection ? "Testing…" : "Test sandbox connection"}
                      </button>
                    </div>
                    {connectionTest && (
                      <div className={`payment-provider-test-result ${connectionTest.type}`}>
                        <strong>{connectionTest.type === "success" ? "Connection successful" : "Connection failed"}</strong>
                        <span>{connectionTest.text}</span>
                        {connectionTest.type === "success" && (
                          <small>
                            OAuth token issued
                            {connectionTest.expiresInSeconds != null ? ` · expires in ${connectionTest.expiresInSeconds} seconds` : ""}
                            {connectionTest.appId ? ` · App ${connectionTest.appId}` : ""}
                          </small>
                        )}
                      </div>
                    )}
                    <div className="payment-provider-info">
                      PayPal is in sandbox mode. This connection test only verifies Hobbli's server-to-server PayPal authentication; it does not connect sellers or enable marketplace checkout.
                    </div>
                  </>
                )}
              </div>
            </>
          ) : <div className="payment-provider-panel payment-provider-empty">Select a payment provider.</div>}
        </div>
      </section>

      <section className="payment-provider-panel payment-provider-direct">
        <div className="payment-provider-heading"><div><h2>Hobbli direct payments</h2><p>Reserved for future payments where Hobbli is the merchant rather than a marketplace seller.</p></div></div>
        <div className="payment-provider-empty">Not configured yet. This is intentionally separate from seller marketplace payments.</div>
      </section>
    </div>
  );
}
